import React, { useState, useEffect, createContext, useContext } from 'react'
import {
  authAPI,
  setAuthTransportMode,
  AuthTransportMode,
  AuthUserResponse,
  ProxyAuthWarning,
} from '../services/api'
import { fetchJsonForAuthMode, setFetchAuthMode } from '../services/authRequest'
import { clearAccessToken, getAccessToken, setAccessToken } from '../services/authHeaders'
import { useActiveBackendTarget } from '../services/remoteBackends/context'
import { translateBackendKey } from '../utils/translateBackendKey'
import { clearRecentPasswordLogin, markRecentPasswordLogin } from '../utils/passkeyPrompt'
import { getDefaultPasskeyDeviceName } from '../utils/passkeyDeviceName'

interface User extends AuthUserResponse {
  id: number
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  hasGlobalPermission: (permission: string) => boolean
  mustChangePassword: boolean
  oidcEnabled: boolean
  oidcProviderName: string | null
  oidcDisableLocalAuth: boolean
  proxyAuthEnabled: boolean
  insecureNoAuthEnabled: boolean
  proxyAuthHeader: string | null
  proxyAuthWarnings: ProxyAuthWarning[]
  authError: string | null
  login: (
    username: string,
    password: string
  ) => Promise<
    | { mustChangePassword: boolean; totpRequired: false }
    | { mustChangePassword: boolean; totpRequired: true; loginChallengeToken: string }
  >
  verifyTotpLogin: (
    loginChallengeToken: string,
    code: string
  ) => Promise<{ mustChangePassword: boolean }>
  loginWithOidcExchangeToken: () => Promise<{ mustChangePassword: boolean }>
  loginWithPasskey: () => Promise<{ mustChangePassword: boolean }>
  canEnrollPasskeyFromRecentLogin: boolean
  enrollPasskeyFromRecentLogin: () => Promise<void>
  canChangePasswordFromRecentLogin: boolean
  changePasswordFromRecentLogin: (newPassword: string) => Promise<void>
  skipPasswordSetup: () => Promise<void>
  markRecentPasswordConfirmation: (password: string) => void
  clearRecentPasskeyEnrollmentState: () => void
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const activeBackendTarget = useActiveBackendTarget()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [oidcEnabled, setOidcEnabled] = useState(false)
  const [oidcProviderName, setOidcProviderName] = useState<string | null>(null)
  const [oidcDisableLocalAuth, setOidcDisableLocalAuth] = useState(false)
  const [proxyAuthEnabled, setProxyAuthEnabled] = useState(false)
  const [insecureNoAuthEnabled, setInsecureNoAuthEnabled] = useState(false)
  const [proxyAuthHeader, setProxyAuthHeader] = useState<string | null>(null)
  const [proxyAuthWarnings, setProxyAuthWarnings] = useState<ProxyAuthWarning[]>([])
  const [authError, setAuthError] = useState<string | null>(null)
  const [recentPasswordForPasskeyPrompt, setRecentPasswordForPasskeyPrompt] = useState<
    string | null
  >(null)

  const clearRecentPasskeyEnrollmentState = () => {
    setRecentPasswordForPasskeyPrompt(null)
  }

  const markRecentPasswordConfirmation = (password: string) => {
    if (!password) return
    markRecentPasswordLogin()
    setRecentPasswordForPasskeyPrompt(password)
  }

  const refreshUser = async (mode: AuthTransportMode = 'jwt') => {
    if (mode !== 'jwt') {
      const response = await fetchJsonForAuthMode('/auth/me', {}, mode)

      if (!response.ok) {
        const error = new Error(`Failed to fetch profile (${response.status})`)
        ;(error as Error & { response?: { status: number; data?: unknown } }).response = {
          status: response.status,
          data: await response.json().catch(() => undefined),
        }
        throw error
      }

      const profile = (await response.json()) as User
      setUser(profile)
      return
    }

    const profileResponse = await authAPI.getProfile()
    setUser(profileResponse.data)
  }

  const extractAuthError = (error: unknown): { status: number | null; detail: unknown } => {
    if (!error || typeof error !== 'object' || !('response' in error)) {
      return { status: null, detail: undefined }
    }

    const response = error.response
    if (!response || typeof response !== 'object') {
      return { status: null, detail: undefined }
    }

    const status = 'status' in response ? Number(response.status) : null
    const detail =
      'data' in response &&
      response.data &&
      typeof response.data === 'object' &&
      'detail' in response.data
        ? response.data.detail
        : undefined

    return { status, detail }
  }

  useEffect(() => {
    let cancelled = false
    const initAuth = async () => {
      setIsLoading(true)
      setUser(null)
      try {
        // First, check if proxy authentication is enabled
        const configResponse = await authAPI.getAuthConfig()
        const {
          oidc_enabled,
          oidc_provider_name,
          oidc_disable_local_auth,
          proxy_auth_enabled,
          insecure_no_auth_enabled,
          proxy_auth_header,
          proxy_auth_health,
        } = configResponse.data
        const authTransportMode: AuthTransportMode = insecure_no_auth_enabled
          ? 'insecure-no-auth'
          : proxy_auth_enabled
            ? 'proxy'
            : 'jwt'

        setOidcEnabled(oidc_enabled ?? false)
        setOidcProviderName(oidc_provider_name ?? null)
        setOidcDisableLocalAuth(oidc_disable_local_auth ?? false)
        setProxyAuthEnabled(proxy_auth_enabled)
        setInsecureNoAuthEnabled(insecure_no_auth_enabled ?? false)
        setProxyAuthHeader(proxy_auth_header ?? null)
        setProxyAuthWarnings(proxy_auth_health?.warnings ?? [])
        setAuthTransportMode(authTransportMode)
        setFetchAuthMode(authTransportMode)
        setAuthError(null)

        if (insecure_no_auth_enabled) {
          clearAccessToken()
          try {
            await refreshUser('insecure-no-auth')
            if (!cancelled) {
              setAuthError(null)
            }
          } catch (error) {
            console.error('Failed to get profile in insecure no-auth mode:', error)
            const { detail } = extractAuthError(error)
            if (!cancelled) {
              setUser(null)
              setAuthError(
                translateBackendKey(
                  detail as
                    | string
                    | { key: string; params?: Record<string, unknown> }
                    | null
                    | undefined
                ) || 'Insecure no-auth mode is enabled but Borg UI could not resolve a local user.'
              )
            }
          }
        } else if (proxy_auth_enabled) {
          // Proxy auth mode: trust the reverse proxy, but fail closed when no identity header arrives.
          let retries = 3
          let success = false

          while (retries > 0 && !success) {
            try {
              await refreshUser()
              success = true
              if (!cancelled) {
                setAuthError(null)
              }
            } catch (error: unknown) {
              console.error('Failed to get profile in proxy auth mode, retrying...', error)
              const { status, detail } = extractAuthError(error)

              if (status === 401 || status === 403) {
                if (!cancelled) {
                  setAuthError(
                    translateBackendKey(
                      detail as
                        | string
                        | { key: string; params?: Record<string, unknown> }
                        | null
                        | undefined
                    )
                  )
                }
                retries = 0
                break
              }

              retries--
              if (retries > 0) {
                await new Promise((resolve) => setTimeout(resolve, 1000)) // Wait 1 second before retry
              }
            }
          }

          if (!success) {
            console.error('Failed to authenticate after retries')
            if (!cancelled) {
              setUser(null)
              setAuthError(
                (existing) =>
                  existing ??
                  'Proxy authentication is enabled but Borg UI did not receive an authenticated user from the reverse proxy.'
              )
            }
          }
        } else {
          // JWT auth mode: check for token
          const token = getAccessToken()
          if (token) {
            try {
              await refreshUser()
            } catch (error) {
              console.error('Failed to get profile with JWT:', error)
              clearAccessToken()
            }
          }
        }
      } catch (error) {
        console.error('Failed to check auth config:', error)
        // Default to JWT auth mode if config check fails
        setAuthTransportMode('jwt')
        setFetchAuthMode('jwt')
        setOidcEnabled(false)
        setOidcProviderName(null)
        setOidcDisableLocalAuth(false)
        setProxyAuthEnabled(false)
        setInsecureNoAuthEnabled(false)
        setProxyAuthWarnings([])
        const token = getAccessToken()
        if (token) {
          try {
            await refreshUser()
          } catch {
            clearAccessToken()
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    initAuth()
    return () => {
      cancelled = true
    }
  }, [activeBackendTarget.id, activeBackendTarget.apiBaseUrl])

  const login = async (
    username: string,
    password: string
  ): Promise<
    | { mustChangePassword: boolean; totpRequired: false }
    | { mustChangePassword: boolean; totpRequired: true; loginChallengeToken: string }
  > => {
    const response = await authAPI.login(username, password)
    const { access_token, must_change_password, totp_required, login_challenge_token } =
      response.data

    if (totp_required && login_challenge_token) {
      setRecentPasswordForPasskeyPrompt(password)
      return {
        mustChangePassword: must_change_password || false,
        totpRequired: true,
        loginChallengeToken: login_challenge_token,
      }
    }

    if (!access_token) {
      throw new Error('Missing access token')
    }

    setAccessToken(access_token)
    markRecentPasswordConfirmation(password)

    await refreshUser()

    return {
      mustChangePassword: must_change_password || false,
      totpRequired: false,
    }
  }

  const loginWithOidcExchangeToken = async (): Promise<{ mustChangePassword: boolean }> => {
    const response = await authAPI.exchangeOidcToken()
    const { access_token, must_change_password } = response.data
    if (!access_token) {
      throw new Error('Missing access token')
    }

    setAccessToken(access_token)
    setAuthTransportMode('jwt')
    setFetchAuthMode('jwt')
    await refreshUser('jwt')

    return { mustChangePassword: must_change_password || false }
  }

  const verifyTotpLogin = async (loginChallengeToken: string, code: string) => {
    const response = await authAPI.verifyTotpLogin(loginChallengeToken, code)
    const { access_token, must_change_password } = response.data
    if (!access_token) {
      throw new Error('Missing access token')
    }
    setAccessToken(access_token)
    markRecentPasswordLogin()
    await refreshUser()
    return { mustChangePassword: must_change_password || false }
  }

  const loginWithPasskey = async () => {
    const { getPasskeyAssertion } = await import('../utils/webauthn')
    const startResponse = await authAPI.beginPasskeyAuthentication()
    const credential = await getPasskeyAssertion(startResponse.data.options)
    const finishResponse = await authAPI.finishPasskeyAuthentication(
      startResponse.data.ceremony_token,
      credential
    )
    const { access_token, must_change_password } = finishResponse.data
    if (!access_token) {
      throw new Error('Missing access token')
    }
    setAccessToken(access_token)
    clearRecentPasswordLogin()
    clearRecentPasskeyEnrollmentState()
    await refreshUser()
    return { mustChangePassword: must_change_password || false }
  }

  const enrollPasskeyFromRecentLogin = async () => {
    if (!recentPasswordForPasskeyPrompt) {
      throw new Error('Missing recent password confirmation')
    }

    const { createPasskeyCredential } = await import('../utils/webauthn')
    const beginResponse = await authAPI.beginPasskeyRegistration(recentPasswordForPasskeyPrompt)
    const credential = await createPasskeyCredential(beginResponse.data.options)

    await authAPI.finishPasskeyRegistration(
      beginResponse.data.ceremony_token,
      credential,
      getDefaultPasskeyDeviceName()
    )

    clearRecentPasskeyEnrollmentState()
  }

  const changePasswordFromRecentLogin = async (newPassword: string) => {
    if (!recentPasswordForPasskeyPrompt) {
      throw new Error('Missing recent password confirmation')
    }

    await authAPI.changePassword(recentPasswordForPasskeyPrompt, newPassword)
    markRecentPasswordConfirmation(newPassword)
    await refreshUser()
  }

  const skipPasswordSetup = async () => {
    await authAPI.skipPasswordSetup()
    await refreshUser()
  }

  const logout = async () => {
    let logoutUrl: string | null | undefined
    try {
      const response = await authAPI.logout()
      logoutUrl = response.data.logout_url
    } catch {
      // Ignore logout errors
    }
    clearAccessToken()
    clearRecentPasswordLogin()
    clearRecentPasskeyEnrollmentState()
    setUser(null)
    if (logoutUrl) {
      window.location.assign(logoutUrl)
    }
  }

  const hasGlobalPermission = (permission: string) => {
    return !!user?.global_permissions?.includes(permission)
  }

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    hasGlobalPermission,
    mustChangePassword: user?.must_change_password || false,
    oidcEnabled,
    oidcProviderName,
    oidcDisableLocalAuth,
    proxyAuthEnabled,
    insecureNoAuthEnabled,
    proxyAuthHeader,
    proxyAuthWarnings,
    authError,
    login,
    verifyTotpLogin,
    loginWithOidcExchangeToken,
    loginWithPasskey,
    canEnrollPasskeyFromRecentLogin: !!recentPasswordForPasskeyPrompt,
    enrollPasskeyFromRecentLogin,
    canChangePasswordFromRecentLogin: !!recentPasswordForPasskeyPrompt,
    changePasswordFromRecentLogin,
    skipPasswordSetup,
    markRecentPasswordConfirmation,
    clearRecentPasskeyEnrollmentState,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
